import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { fileUploadConfig } from '@/config'
import { cn } from '@/utils/cn'
import { FileJson, FileSpreadsheet, HelpCircle, Upload, X } from 'lucide-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'

interface FileUploadProps {
  onFileAccepted: (file: File) => void
  onFileRemoved?: () => void
  acceptedFile?: File
  className?: string
  error?: string
  isValidating?: boolean
}

export function FileUpload({ 
  onFileAccepted, 
  onFileRemoved,
  acceptedFile,
  className,
  error,
  isValidating
}: FileUploadProps) {
  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file) {
        onFileAccepted(file);
      }
    } else if (fileRejections.length > 0) {
      // Handle file rejection reasons
      const rejection = fileRejections[0]
      const errorMessages = rejection.errors.map((e: any) => {
        if (e.code === 'file-too-large') {
          return `File is too large. Maximum size is ${(fileUploadConfig.maxFileSize / 1024 / 1024).toFixed(0)}MB`
        }
        if (e.code === 'file-invalid-type') {
          return 'Invalid file type. Please use .json or .csv files'
        }
        return e.message
      })
      // You might want to pass this error up to parent component
      console.error('File rejected:', errorMessages.join(', '))
    }
  }, [onFileAccepted])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: fileUploadConfig.acceptedFormats.mimeTypes,
    maxFiles: 1,
    multiple: false,
    maxSize: fileUploadConfig.maxFileSize,
    // Keep whole card clickable, but allow an explicit button too
    noClick: false,
    noKeyboard: false,
  })

  if (acceptedFile) {
    return (
      <div 
        className={cn("border-2 border-dashed rounded-lg p-6", className)}
        role="region"
        aria-label="File uploaded"
        data-testid="file-upload-status"
      >
        <div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              {acceptedFile.name.endsWith('.json') ? (
                <FileJson className="h-8 w-8 text-blue-500 flex-shrink-0" aria-hidden="true" />
              ) : (
                <FileSpreadsheet className="h-8 w-8 text-green-500 flex-shrink-0" aria-hidden="true" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" title={acceptedFile.name}>
                  {acceptedFile.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {(acceptedFile.size / 1024).toFixed(2)} KB
                  {isValidating && ' - Validating...'}
                </p>
              </div>
            </div>
            {onFileRemoved && !isValidating && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onFileRemoved}
                className="h-8 w-8 flex-shrink-0"
                aria-label="Remove file"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
          {error && (
            <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "rounded-lg p-8 text-center cursor-pointer transition-all duration-200",
          "border-2 border-dashed border-dark-600/50 bg-dark-800/30 hover:border-dark-500/70 hover:bg-dark-800/50",
          isDragActive && "border-brand-blue/60 ring-2 ring-brand-blue/20 bg-brand-blue/5 border-solid"
        )}
        role="button"
        tabIndex={0}
        aria-label="Upload file - drag and drop or click to select"
        aria-describedby="file-upload-description"
      >
        <input {...getInputProps()} aria-label="File input" data-testid="file-upload-input" />
        <Upload className="mx-auto h-10 w-10 text-muted-foreground/70 mb-3" aria-hidden="true" />
        {isDragActive ? (
          <p className="text-sm text-brand-blue font-medium" role="status" aria-live="polite">
            Drop the file here...
          </p>
        ) : (
          <div id="file-upload-description" className="space-y-2">
            <p className="text-sm font-medium text-white">Drag & drop a TradingView export or choose a file</p>
            <p className="text-xs text-muted-foreground">
              JSON or CSV • Max {(fileUploadConfig.maxFileSize / 1024 / 1024).toFixed(0)} MB • Processed in your browser
            </p>
            <p className="sr-only">Or press Enter or Space to open file dialog</p>
          </div>
        )}
        <div className="mt-4 flex items-center justify-center">
          <Button
            type="button"
            onClick={(e) => { e.stopPropagation(); open(); }}
            variant="outline"
            className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/30"
          >
            Choose file
          </Button>
        </div>
      </div>

      {/* Format Help Section */}
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="format-help" className="border-dark-700/30 border rounded-lg bg-dark-800/20">
          <AccordionTrigger className="text-xs text-muted-foreground hover:text-white hover:no-underline px-3 py-2">
            <span className="flex items-center gap-2">
              <HelpCircle className="h-3.5 w-3.5" />
              Supported data formats
            </span>
          </AccordionTrigger>
          <AccordionContent className="text-xs text-muted-foreground px-3 pb-3">
            <div className="space-y-3">
              <p className="text-white/80">
                Upload OHLCV candle data in JSON or CSV format. The optimizer supports multiple formats:
              </p>

              <div>
                <p className="text-white/90 font-medium mb-1">Standard Format (TradingView, etc.):</p>
                <pre className="bg-dark-900/50 rounded p-2 overflow-x-auto text-[10px] leading-relaxed">
{`[
  { "timestamp": 1672531200000, "open": 100.5, "high": 102.0, "low": 99.8, "close": 101.2, "volume": 1500 },
  { "timestamp": 1672617600000, "open": 101.2, "high": 103.5, "low": 100.9, "close": 103.1, "volume": 2100 }
]`}
                </pre>
              </div>

              <div>
                <p className="text-white/90 font-medium mb-1">Databento Format:</p>
                <pre className="bg-dark-900/50 rounded p-2 overflow-x-auto text-[10px] leading-relaxed">
{`{"hd":{"ts_event":"2025-12-14T23:00:00.000000000Z",...},"open":"6831.0","high":"6838.0","low":"6830.0","close":"6837.75","volume":"810","symbol":"ESZ5"}
{"hd":{"ts_event":"2025-12-14T23:01:00.000000000Z",...},"open":"6837.5","high":"6837.75","low":"6834.0","close":"6834.75","volume":"225","symbol":"ESZ5"}`}
                </pre>
              </div>

              <div className="text-[10px] space-y-1 border-t border-dark-700/30 pt-2 mt-2">
                <p className="text-white/70 font-medium">Required fields:</p>
                <ul className="list-disc list-inside text-muted-foreground/80 space-y-0.5">
                  <li><code className="text-brand-blue/80">timestamp</code> or <code className="text-brand-blue/80">time</code> or <code className="text-brand-blue/80">date</code> — Unix ms, seconds, or ISO 8601</li>
                  <li><code className="text-brand-blue/80">open</code>, <code className="text-brand-blue/80">high</code>, <code className="text-brand-blue/80">low</code>, <code className="text-brand-blue/80">close</code> — numeric values</li>
                  <li><code className="text-brand-blue/80">volume</code> — numeric (optional, defaults to 0)</li>
                </ul>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
